import { WaypointOptions } from '../gallery/TourModeModal';

export const ROOT_FOLDER_ID = 'root';
export const ROOT_FOLDER_NAME = 'Upload Management';

export type ContentTypeName = 'Folder' | 'Panorama' | 'WebStandalone' | 'Gallery' | 'root';

export type ContentDisplayName =
  | 'Panorama Mono'
  | 'Panorama Stereo'
  | 'Web Standalone'
  | 'Folder'
  | 'Panorama Gallery';

export interface Content<T extends ContentTypeName = any> {
  guid: string;
  parentGuid?: string | null;
  name?: string;
  createdAt: string;
  modifiedAt?: string;
  contentType: {
    guid: string;
    name: T;
  };
  sharedAccessKey: string;
  parentAccessKey?: string | null;
  chaosCollaborationUrl?: string | null;
}

export type WebStandaloneContent = Content<'WebStandalone'>;

export type PanoramaContent = Content<'Panorama'>;

export type FolderContent = Content<'Folder'>;

export type GalleryContent = Content<'Gallery'>;

export interface RootFolder extends Content<'root'> {
  contents: Content[];
}

export interface Folder extends Content<'Folder'> {
  contents: Content[];
}

interface Detail<T = any> {
  detailType: T;
}

export interface PanoramaDetails extends Detail<'Panorama'> {
  panoramaGuid: string;
  title: string;
  projectName: string;
  viewLink: string;
  thumbnailDownloadUrl: string;
  isStereo: boolean;
  imageDownloadUrl: string;
  camera: {
    x: number;
    y: number;
    z: number;
    pitch: number;
    roll: number;
    yaw: number;
  };
}

export interface WebStandaloneDetails extends Detail<'WebStandalone'> {
  viewLink: string;
}

export type ContentDetails = Record<string, Detail | null>;

export interface Gallery {
  name: string;
  panoramas: PanoramaDetails[];
  guid: string;
  tourMode: WaypointOptions;
}

export const isFolder = (folderLike: Folder | RootFolder): folderLike is Folder =>
  folderLike.contentType.name === 'Folder' && 'contents' in folderLike;
export const isRootFolder = (folderLike: Folder | RootFolder): folderLike is RootFolder =>
  folderLike.contentType.name === 'root';

export interface FolderHierarchy {
  subHierarchy: FolderHierarchy[];
  guid: string;
  parentGuid?: string | null;
  name?: string;
  createdAt: string;
  modifiedAt?: string;
}

export const isFolderContent = (content: Content): content is FolderContent =>
  content.contentType.name === 'Folder' && !('contents' in content);
export const isPanoramaContent = (content: Content): content is PanoramaContent =>
  content.contentType.name === 'Panorama';
export const isWebStandaloneContent = (content: Content): content is WebStandaloneContent =>
  content.contentType.name === 'WebStandalone';
export const isGalleryContent = (content: Content): content is GalleryContent =>
  content.contentType.name === 'PanoramaTour';

export const isPanoramaDetails = (details: Detail | null): details is PanoramaDetails =>
  details?.detailType === 'Panorama';
export const isWebstandaloneDetails = (details: Detail | null): details is WebStandaloneDetails =>
  details?.detailType === 'WebStandalone';

export const getDetailFromContent = <T extends Content>(
  content: T,
  contentDetails: ContentDetails,
): T extends PanoramaContent
  ? PanoramaDetails
  : T extends WebStandaloneContent
  ? WebStandaloneDetails
  : never => {
  const details = contentDetails[content.guid];
  if (details) {
    return contentDetails[content.guid] as any;
  }
  throw new Error(`Not details found for ${content.contentType.name} guid=${content.guid}`);
};

export type UploadView = 'default' | 'shared';

export type CurrentFolder = Folder | RootFolder | null;
export type CurrentFolderHierarchy = FolderHierarchy | undefined;

export interface CurrentUploadContent {
  folderLike: CurrentFolder;
  contentDetails: ContentDetails;
}
